import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";

// Helper function to check if the string is a valid URL
const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

// Helper to convert seconds to "Xh Ym" format
function secondsToHuman(secs) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  let str = "";
  if (h > 0) str += `${h}h `;
  if (m > 0) str += `${m}m`;
  if (!str) str = "0m";
  return str.trim();
}

export default function SubmissionForm({ user }) {
  const [siteUrl, setSiteUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [projectName, setProjectName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [description, setDescription] = useState("");
  const DESCRIPTION_LIMIT = 100;

  useEffect(() => {
    if (!user?.slackId) return;
    const fetchProjects = async () => {
      try {
        const res = await axios.get(
          `https://hackatime.hackclub.com/api/v1/users/${user.slackId}/stats?start_date=2025-12-1&features=projects`
        );
        setProjects(res.data.data.projects || []);
      } catch (e) {
        setProjects([]);
      }
    };
    fetchProjects(); 
  }, [user?.slackId]); 

  const sanitizeUrl = (url) => {
    // If the URL does not start with 'http://' or 'https://', add 'https://'
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      return "https://" + url;
    }
    return url;
  };

  const submit = async () => {
    if (
      !siteUrl ||
      !imageUrl ||
      !sourceUrl ||
      !description.trim() ||
      !projectName.trim()
    ) {
      setMessage("Please fill in all fields!");
      return;
    }
    if (description.length > DESCRIPTION_LIMIT) {
      setMessage(
        `Description can not be greater than ${DESCRIPTION_LIMIT} characters.`
      );
      return;
    }

    if (!isValidUrl(siteUrl)) {
      setMessage(
        "Invalid demo URL. Please provide a valid URL. (URL should start with http:// or https://)"
      );
      return;
    }

    if (!isValidUrl(imageUrl)) {
      setMessage("Invalid image URL. Please provide a valid image URL.");
      return;
    }

    if (!isValidUrl(sourceUrl)) {
      setMessage("Invalid source code URL. Please provide a valid URL.");
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      const sanitizedSiteUrl = sanitizeUrl(siteUrl);

      await axios.post(
        `${API_BASE_URL}/submit`,
        {
          siteUrl: sanitizedSiteUrl,
          imageUrl,
          sourceUrl,
          projectName,
          description,
          hackatime: {
            totalTime: selectedTotalTime,
            projects: selectedProjects.map((p) => ({
              name: p.name,
              text: p.text,
              total_seconds: p.total_seconds,
            })),
          },
        },
        { withCredentials: true }
      );

      setMessage("Submission successful! Reloading...");
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      setMessage(err.response?.data?.message || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const url = e.target.value;
    setImageUrl(url);

    // Preview the image if it's a valid URL
    if (isValidUrl(url)) {
      setImagePreview(url);
    } else {
      setImagePreview("");
    }
  };

  // Calculate total time from selected projects
  const selectedTotalSeconds = selectedProjects.reduce(
    (sum, p) => sum + (p.total_seconds || 0),
    0
  );
  const selectedTotalTime = secondsToHuman(selectedTotalSeconds);

  const descriptionWarning =
    description.length > DESCRIPTION_LIMIT - 20 && description.length <= DESCRIPTION_LIMIT;
  const isSuccessMessage = useMemo(
    () => message && message.toLowerCase().includes("success"),
    [message]
  );

  return (
    <div className="parchment-form">
      <h2>Submit Your Project</h2>

      <div className="parchment-field">
        <label htmlFor="siteUrl">Demo URL</label>
        <input
          id="siteUrl"
          placeholder="(URL should start with https:// or http://)"
          value={siteUrl}
          onChange={(e) => setSiteUrl(e.target.value)}
        />
      </div>

      <div className="parchment-field">
        <label htmlFor="sourceUrl">Source Code URL</label>
        <input
          id="sourceUrl"
          placeholder="e.g GitHub Repo link"
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
        />
      </div>

      <div className="parchment-field">
        <label htmlFor="imageUrl">Image URL</label>
        <input
          id="imageUrl"
          placeholder="(you can use #cdn, or any other cdn)"
          value={imageUrl}
          onChange={handleImageChange}
        />
      </div>

      {imagePreview && (
        <div className="parchment-field">
          <h4>Image Preview</h4>
          <img src={imagePreview} alt="Preview" className="parchment-image" />
        </div>
      )}

      <div className="parchment-field">
        <label htmlFor="description">Project Description</label>
        <textarea
          id="description"
          className="parchment-textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          maxLength={DESCRIPTION_LIMIT}
          rows={4}
        />
        <div className={`parchment-helper ${descriptionWarning ? "warn" : ""}`}>
          {description.length}/{DESCRIPTION_LIMIT} characters
        </div>
      </div>

      <div className="parchment-field">
        <label htmlFor="projectName">Project Name</label>
        <input
          id="projectName"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          required
          maxLength={60}
          placeholder="Enter your project name"
        />
      </div>

      {projects.length > 0 && (
        <div className="parchment-field">
          <label htmlFor="hackatime-projects">Link Hackatime Projects (optional)</label>
          <select
            id="hackatime-projects"
            onChange={(e) => {
              const selectedName = e.target.value;
              const project = projects.find((p) => p.name === selectedName);
              if (project && !selectedProjects.find((p) => p.name === project.name)) {
                setSelectedProjects([
                  ...selectedProjects,
                  {
                    name: project.name,
                    text: project.text,
                    total_seconds: project.total_seconds,
                  },
                ]);
              }
              e.target.value = "";
            }}
          >
            <option value="">-- Select a project --</option>
            {projects.map((proj) => (
              <option key={proj.name} value={proj.name}>
                {proj.name} ({proj.text})
              </option>
            ))}
          </select>

          {/* Show selected project "pills" */}

          {selectedProjects.length > 0 && (
            <div className="parchment-pills">
              {selectedProjects.map((proj) => (
                <span className="parchment-pill" key={proj.name}>
                  {proj.name} ({proj.text})
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedProjects(
                        selectedProjects.filter((p) => p.name !== proj.name)
                      )
                    }
                    aria-label={`Remove ${proj.name}`}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}

          {selectedTotalTime && (
            <div className="parchment-summary">
              <b>Total Hackatime:</b> {selectedTotalTime}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        className="parchment-submit"
        onClick={submit}
        disabled={loading}
      >
        {loading ? "Submitting..." : "Submit"}
      </button>

      {message && (
        <p className={`parchment-message ${isSuccessMessage ? "success" : ""}`}>
          {message}
        </p>
      )}
    </div>
  );
}
